import React, { useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "util/locale";
import styles from "./kpiView.module.scss";
import {
  formatSparklineAxisDate,
  formatSparklineTooltipDate,
  pickSparklineWeeklyTicks,
} from "./kpiViewUtils";

const CHART_W = 200;
const CHART_H = 56;
const PAD = 4;

function buildSmoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) {
    return `M ${points[0][0]},${points[0][1]}`;
  }

  let path = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
  }
  return path;
}

export default function KpiSparkline({
  values = [],
  dates = [],
  tickIntervalDays = 7,
  granularity = "daily",
  bucketDays,
  onPointClick,
}) {
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const { linePath, areaPath, seriesPoints, axisTicks } = useMemo(() => {
    if (!values.length) {
      return {
        linePath: "",
        areaPath: "",
        seriesPoints: [],
        axisTicks: [],
      };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const step =
      values.length > 1 ? (CHART_W - PAD * 2) / (values.length - 1) : 0;

    const seriesPoints = values.map((value, index) => {
      const x = PAD + index * step;
      const y = CHART_H - PAD - ((value - min) / range) * (CHART_H - PAD * 2);
      return {
        index,
        value,
        date: dates[index],
        x,
        y,
      };
    });

    const pointCoords = seriesPoints.map((p) => [p.x, p.y]);
    const line = buildSmoothPath(pointCoords);
    if (!line) {
      return {
        linePath: "",
        areaPath: "",
        seriesPoints: [],
        axisTicks: [],
      };
    }

    const last = pointCoords[pointCoords.length - 1];
    const first = pointCoords[0];
    const area = `${line} L ${last[0]},${CHART_H - PAD} L ${first[0]},${CHART_H - PAD} Z`;

    const weeklyTicks = pickSparklineWeeklyTicks(dates, tickIntervalDays);
    const axisTicks = weeklyTicks.map(({ index, date }) => ({
      index,
      date,
      label: formatSparklineAxisDate(date),
      x: seriesPoints[index]?.x ?? PAD,
    }));

    return { linePath: line, areaPath: area, seriesPoints, axisTicks };
  }, [values, dates, tickIntervalDays]);

  const resolveIndexFromClientX = useCallback(
    (clientX) => {
      const el = chartRef.current;
      if (!el || !values.length) return null;
      const rect = el.getBoundingClientRect();
      if (!rect.width) return null;
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      if (values.length === 1) return 0;
      return Math.round(ratio * (values.length - 1));
    },
    [values.length]
  );

  const handleMouseMove = useCallback(
    (event) => {
      const index = resolveIndexFromClientX(event.clientX);
      setActiveIndex(index);
    },
    [resolveIndexFromClientX]
  );

  const handleMouseLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const handleClick = useCallback(
    (event) => {
      if (!onPointClick) return;
      const index = resolveIndexFromClientX(event.clientX);
      if (index == null) return;
      const point = seriesPoints[index];
      if (!point) return;
      onPointClick({
        index: point.index,
        date: point.date,
        value: point.value,
      });
    },
    [onPointClick, resolveIndexFromClientX, seriesPoints]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!onPointClick || (event.key !== "Enter" && event.key !== " ")) return;
      event.preventDefault();
      const index = activeIndex ?? seriesPoints.length - 1;
      const point = seriesPoints[index];
      if (!point) return;
      onPointClick({
        index: point.index,
        date: point.date,
        value: point.value,
      });
    },
    [activeIndex, onPointClick, seriesPoints]
  );

  const activePoint =
    activeIndex != null ? seriesPoints[activeIndex] : null;
  const activeLeftPct =
    activePoint && values.length > 1
      ? (activePoint.index / (values.length - 1)) * 100
      : 50;

  if (!values.length) {
    return <div className={styles.sparklineWrap} />;
  }

  return (
    <div className={styles.sparklineWrap}>
      <div
        className={`${styles.sparklineChartInteractive} ${
          onPointClick ? styles.sparklineChartClickable : ""
        }`}
        ref={chartRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={onPointClick ? "button" : undefined}
        tabIndex={onPointClick ? 0 : undefined}
        aria-label={
          onPointClick
            ? granularity === "rolling" || granularity === "weekly"
              ? Number(bucketDays) === 30
                ? t("team_kpi_view.sparkline_30", "30-day interval review chart")
                : t("team_kpi_view.sparkline_7", "7-day interval review chart")
              : t("team_kpi_view.sparkline_daily", "Daily review chart")
            : undefined
        }
      >
        {activePoint && (
          <div
            className={styles.sparklineTooltip}
            style={{ left: `${activeLeftPct}%` }}
          >
            <span className={styles.sparklineTooltipDate}>
              {formatSparklineTooltipDate(activePoint.date, {
                granularity,
                bucketDays,
              })}
            </span>
            <span className={styles.sparklineTooltipValue}>
              {Number(activePoint.value).toLocaleString(getIntlLocale(i18n.language))} {t("team_kpi_view.review_count_suffix", "reviews")}
              {granularity === "rolling" || granularity === "weekly"
                ? Number(bucketDays) === 30
                  ? ` ${t("team_kpi_view.sparkline_30_suffix", "(30-day)")}`
                  : ` ${t("team_kpi_view.sparkline_7_suffix", "(7-day)")}`
                : ""}
            </span>
          </div>
        )}
        <svg
          className={styles.sparkline}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          preserveAspectRatio="none"
        >
          {axisTicks.map((tick) => (
            <line
              key={`grid-${tick.index}`}
              x1={tick.x}
              x2={tick.x}
              y1={PAD}
              y2={CHART_H - PAD}
              className={styles.sparklineGridLine}
            />
          ))}
          {areaPath && (
            <path
              d={areaPath}
              className={styles.sparklineArea}
              stroke="none"
            />
          )}
          <path
            d={linePath}
            className={styles.sparklinePath}
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {activePoint && (
            <>
              <line
                x1={activePoint.x}
                x2={activePoint.x}
                y1={PAD}
                y2={CHART_H - PAD}
                className={styles.sparklineActiveLine}
              />
              <circle
                cx={activePoint.x}
                cy={activePoint.y}
                r="3.5"
                className={styles.sparklineActiveDot}
              />
            </>
          )}
        </svg>
      </div>
      {axisTicks.length > 0 && (
        <div className={styles.sparklineAxis} aria-hidden>
          {axisTicks.map((tick) => {
            const leftPct =
              values.length > 1 ? (tick.index / (values.length - 1)) * 100 : 50;
            return (
              <span
                key={`${tick.index}-${tick.date}`}
                className={styles.sparklineXLabel}
                style={{ left: `${leftPct}%` }}
              >
                {tick.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
